import {useState} from 'react'
import type {ReactNode} from 'react'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {DescargarEvidencia} from '@/components/descargar-evidencia'
import {HelpIcon} from '@/components/help-icon'
import {DownloadGlyph, LockGlyph} from '@/components/ui/icons'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {PageHeading} from '@/components/page-heading'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {ListEmptyState, SectionCard} from '@/components/section-card'
import {MEMBER_PAGE_SIZE, useMemberSoportes, type MemberEvidence} from '@/hooks/empresa/mi_espacio/use-member'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {rutaDeMiAporte} from '@/hooks/sgsst/evidencias/use-solicitudes'
import {formatShortDate} from '@/lib/risk'

/** El rótulo va en `sr-only` y no en un popover: así existe para el lector de pantalla y para Ctrl+F. */
function CandadoSellado({label}: {label: string}) {
  return (
    <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground">
      <LockGlyph size={15} />
      <span className="sr-only">{label}</span>
    </span>
  )
}

function fileKind(filename: string): string {
  const ext = filename.split('.').pop()?.toUpperCase()
  return ext && ext.length <= 4 ? ext : 'DOC'
}

function SoporteRow({
  glyph,
  children,
  tail,
  testId,
}: {
  glyph: ReactNode
  children: ReactNode
  tail: ReactNode
  testId: string
}) {
  return (
    <div
      className="surface-divider flex flex-wrap items-start gap-x-3 gap-y-2 border-b px-5 py-3.5 last:border-b-0"
      data-testid={testId}>
      {glyph}
      <div className="min-w-0 flex-1 basis-56">{children}</div>
      <div className="flex shrink-0 items-center gap-2">{tail}</div>
    </div>
  )
}

function CompanyRow({row}: {row: MemberEvidence}) {
  const {t} = useTranslation()
  return (
    <SoporteRow
      testId="soporte-company-row"
      glyph={<CandadoSellado label={t('member.soportes.notVisible')} />}
      tail={
        <>
          <Tag tone="green">{t('member.soportes.upToDate')}</Tag>
          <span className="font-mono text-xs text-muted-foreground">
            {formatShortDate(row.uploaded_at.slice(0, 10))}
          </span>
        </>
      }>
      <p className="text-[13.5px] font-medium">{t('member.soportes.companyLabel')}</p>
    </SoporteRow>
  )
}

function OwnRow({businessId, row}: {businessId: string; row: MemberEvidence}) {
  const {t} = useTranslation()
  const ruta = rutaDeMiAporte(businessId, row.evidence_id ?? '')
  const godReadOnly = useGodModeReadOnly()
  // La persona abre su documento de salud; con ver-como la API no lo abre.
  const reservado = !!row.de_salud && godReadOnly
  return (
    <SoporteRow
      testId="soporte-own-row"
      glyph={
        <span
          aria-hidden="true"
          className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-md bg-secondary font-mono text-[9px] font-semibold tracking-wide text-destructive">
          {fileKind(row.filename ?? '')}
        </span>
      }
      tail={
        !reservado && (
          <DescargarEvidencia
            ruta={ruta}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-primary hover:underline">
            <DownloadGlyph />
            {t('member.soportes.download')}
          </DescargarEvidencia>
        )
      }>
      <div className="flex flex-wrap items-center gap-2">
        <DescargarEvidencia
          ruta={ruta}
          reservado={reservado}
          className="min-w-0 break-all text-[13.5px] font-semibold text-primary hover:underline"
          testId="soporte-download">
          {row.filename}
        </DescargarEvidencia>
        <Tag tone="neutral">{t('member.soportes.contributedByYou')}</Tag>
      </div>
      <p className="mt-0.5 font-mono text-[11.5px] text-muted-foreground">
        {t('member.soportes.uploadedOn', {date: formatShortDate(row.uploaded_at.slice(0, 10))})}
      </p>
    </SoporteRow>
  )
}

type StandardGroup = {year: number; isClosed: boolean; code: string; name: string; rows: MemberEvidence[]}

/** El año va de sello en cada tarjeta: el servidor pagina la lista plana y una sección por año se partiría. */
function group(rows: MemberEvidence[]): StandardGroup[] {
  const grupos: StandardGroup[] = []
  for (const row of rows) {
    let std = grupos.find((g) => g.year === row.year && g.code === row.standard_code)
    if (!std) {
      std = {year: row.year, isClosed: row.is_closed, code: row.standard_code, name: row.standard_name, rows: []}
      grupos.push(std)
    }
    std.rows.push(row)
  }
  return grupos
}

export function MiSoportesBody({businessId}: {businessId: string | undefined}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const {data: sobre, isLoading} = useMemberSoportes(businessId, page)
  const rows = sobre?.items

  return (
    <>
      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !sobre || sobre.total === 0 ? (
        <div className="surface mx-auto max-w-2xl">
          <ListEmptyState page title={t('member.soportes.emptyTitle')} />
        </div>
      ) : (
        <div className="space-y-3">
          {group(rows ?? []).map((std) => (
            <SectionCard
              key={`${std.year}-${std.code}`}
              bodyClassName="p-0"
              className="overflow-hidden"
              actions={
                std.rows.some((r) => r.source === 'empresa') ? (
                  <HelpIcon label={t('member.soportes.notVisibleLabel')} testId="soporte-company-tip">
                    {t('member.soportes.notVisible')}
                  </HelpIcon>
                ) : undefined
              }
              title={
                <span className="flex flex-wrap items-baseline gap-2">
                  <span className="font-mono text-xs font-semibold text-primary">{std.code}</span>
                  <span className="text-base">{std.name}</span>
                  <span className="flex items-center gap-2" data-testid="soporte-year">
                    <span className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
                      {t('member.soportes.year', {year: std.year})}
                    </span>
                    {std.isClosed && <Tag tone="neutral">{t('member.soportes.closed')}</Tag>}
                  </span>
                </span>
              }>
              {std.rows.map((row, i) =>
                row.source === 'empresa' ? (
                  <CompanyRow key={i} row={row} />
                ) : (
                  <OwnRow key={i} businessId={businessId ?? ''} row={row} />
                )
              )}
            </SectionCard>
          ))}
          <PaginationBar page={page} pageSize={MEMBER_PAGE_SIZE} total={sobre.total} onPageChange={setPage} />
        </div>
      )}
    </>
  )
}

export function MiSoportesPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()

  return (
    <div className="space-y-5">
      <PageHeading title={t('member.soportes.title')} />
      <MiSoportesBody businessId={businessId} />
    </div>
  )
}
